import React from 'react';
import { motion } from 'framer-motion';
import { Droplets, Wind, Thermometer, Sun } from 'lucide-react';

const BentoCard = ({ icon: Icon, title, value, delay, className }) => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }} // Changed from whileInView to animate for safety
        transition={{ duration: 0.5, delay }}
        className={`glass-panel ${className}`}
        style={{
            padding: '2.5rem',
            borderRadius: '24px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            position: 'relative',
            overflow: 'hidden',
            transition: 'border-color 0.3s'
        }}
    >
        {/* Subtle gradient blob background */}
        <div style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '150px',
            height: '150px',
            background: 'radial-gradient(circle, rgba(255,255,255,0.03) 0%, rgba(0,0,0,0) 70%)',
            pointerEvents: 'none'
        }} />

        <div>
            <div style={{
                display: 'inline-flex',
                padding: '1rem',
                borderRadius: '16px',
                marginBottom: '1.5rem',
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                color: 'var(--accent)'
            }}>
                <Icon size={28} strokeWidth={1.5} />
            </div>
            <h3 style={{ fontSize: '1.5rem', marginBottom: '0.5rem', fontWeight: 500 }}>{title}</h3>
        </div>

        <div>
            <p style={{ color: 'var(--text-muted)', fontSize: '1rem' }}>{value}</p>

            {/* Decorative Line */}
            <div style={{
                height: '2px',
                width: '40px',
                background: 'var(--accent)',
                marginTop: '1.5rem',
                opacity: 0.5
            }} />
        </div>
    </motion.div>
);

const Features = () => {
    return (
        <section style={{ padding: '8rem 2rem', position: 'relative' }}>
            <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                <div style={{ marginBottom: '6rem' }}>
                    <motion.span
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        style={{
                            color: 'var(--accent)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.2em',
                            fontSize: '0.85rem',
                            display: 'block',
                            marginBottom: '1rem'
                        }}
                    >
                        System capabilities
                    </motion.span>
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        style={{ fontSize: '3rem', maxWidth: '600px', lineHeight: 1.1 }}
                    >
                        Complete awareness for your ecosystem.
                    </motion.h2>
                </div>

                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(12, 1fr)',
                    gridTemplateRows: 'repeat(2, minmax(300px, auto))',
                    gap: '1.5rem'
                }}>
                    {/* Card 1: Large spanning 7 columns */}
                    <div style={{ gridColumn: 'span 7' }} className="bento-large">
                        <BentoCard
                            icon={Droplets}
                            title="Soil Moisture"
                            value="Capacitive sensors measure hydration at the root level, ensuring precision watering."
                            delay={0}
                            className="h-full"
                        />
                    </div>

                    {/* Card 2: Medium spanning 5 columns */}
                    <div style={{ gridColumn: 'span 5' }}>
                        <BentoCard
                            icon={Sun}
                            title="Light Intensity"
                            value="Track PAR ranges to optimize photosynthesis."
                            delay={0.1}
                            className="h-full"
                        />
                    </div>

                    {/* Card 3: Medium spanning 5 columns */}
                    <div style={{ gridColumn: 'span 5' }}>
                        <BentoCard
                            icon={Thermometer}
                            title="Thermal Balance"
                            value="Alerts for sudden temperature shifts that stress plants."
                            delay={0.2}
                            className="h-full"
                        />
                    </div>

                    {/* Card 4: Large spanning 7 columns */}
                    <div style={{ gridColumn: 'span 7' }} className="bento-large">
                        <BentoCard
                            icon={Wind}
                            title="Ambient Humidity"
                            value="Monitor VPD (Vapor Pressure Deficit) for optimal growth conditions."
                            delay={0.3}
                            className="h-full"
                        />
                    </div>
                </div>
            </div>

            {/* Inline styles for the grid responsive behavior (simple approach) */}
            <style>{`
        @media (max-width: 900px) {
          .bento-large, div[style*="gridColumn: 'span 7'"], div[style*="gridColumn: 'span 5'"] {
            grid-column: span 12 !important;
          }
        }
      `}</style>
        </section>
    );
};

export default Features;
